import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useState, useContext } from "react";
import AngledAxisTick from '../AngledAxisTick';
import ScoreBlock from '../../analysis/ScoreBlock';
import MatrixLineTooltip from './MatrixLineTooltip';
import AnalyticsEmptyState from "../../dashboard/AnalyticsEmptyState";
import LiveReportRenderContext from '../../../StateManagment/LiveReportRenderContext';

const MatrixDevelopmentLineChart = ({ data, lowerScale, upperScale, totalResponses, effectId, benchmark, benchmarkLoading, benchmarkUnavailableReason, infoPopoverContent, graphColor }) => {
  const [hoveredLine, setHoveredLine] = useState(null);
  // In a live report, "totale groei" is its own toggle option, so the heading
  // above the chart is suppressed. Dashboard/analysis keep it.
  const inLiveReport = useContext(LiveReportRenderContext);

  if (!data || data.length === 0) {
    return <AnalyticsEmptyState />;
  }

  const uniqueRowTitles = Object.keys(data[0]).filter((key) => key !== "name");

  const calculateTotalGrowth = () => {
    const totalValues = data.map((item) =>
      uniqueRowTitles.reduce((sum, key) => {
        const value = item[key]?.value;
        const adjustedValue =
          item[key]?.negative && value !== undefined
            ? upperScale - value + lowerScale
            : value ?? 0;
        return sum + adjustedValue;
      }, 0)
    );

    if (totalValues.length > 1) {
      const initialTotal = totalValues[0];
      const finalTotal = totalValues[totalValues.length - 1];
      const maxTotal = uniqueRowTitles.length * (upperScale - lowerScale);

      return ((finalTotal - initialTotal) / maxTotal) * 100;
    }
    return 0;
  };

  const totalGrowth = calculateTotalGrowth().toFixed(1);

  const CustomizedLegend = ({ payload }) => (
    <ul>
      {payload.map((entry, index) => {
        const rowTitle = entry.dataKey;
        return (
          <li
            key={`item-${index}`}
            onMouseEnter={() => setHoveredLine(rowTitle)}
            onMouseLeave={() => setHoveredLine(null)}
            style={{
              cursor: "pointer",
              fontWeight: hoveredLine === rowTitle ? "bold" : "normal",
              color: entry.color,
            }}
          >
            {rowTitle}
          </li>
        );
      })}
    </ul>
  );

  return (
    <>
      {!inLiveReport && <ScoreBlock totalGrowth={Number(totalGrowth)} benchmark={benchmark} benchmarkLoading={benchmarkLoading} benchmarkUnavailableReason={benchmarkUnavailableReason} effectId={effectId} />}
      <svg style={{ position: "absolute", width: 0, height: 0 }}>
        <defs>
          {uniqueRowTitles.map((rowTitle, index) => (
            <filter
              id={`glow-${index}`}
              key={`glow-${index}`}
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur stdDeviation="15" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          ))}
        </defs>
      </svg>
      <ResponsiveContainer width="100%" height={500}>
        <LineChart
          data={data.map((item) =>
            Object.fromEntries(
              Object.entries(item).map(([key, value]) => {
                if (key === "name") {
                  return [key, value];
                } else {
                  const adjustedValue =
                    value?.negative && value?.value !== undefined
                      ? upperScale - value.value + lowerScale
                      : value?.value ?? null;

                  return [key, adjustedValue];
                }
              })
            )
          )}
          margin={{
            top: 5,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <XAxis
          dataKey="name"
          tick={<AngledAxisTick fill={graphColor || undefined} />}
          interval={0}
          height={70}
          axisLine={false}
          tickLine={false}
          stroke={graphColor || 'black'}
          />
          <YAxis
          domain={[0, upperScale]}
          axisLine={false}
          tickLine={false}
          stroke={graphColor || 'black'}
          tick={{ fill: graphColor || undefined }}
          />
          <Tooltip content={(props) => <MatrixLineTooltip {...props} />} />
          <Legend content={<CustomizedLegend />} verticalAlign="bottom" wrapperStyle={{ color: graphColor || undefined }} />
          {uniqueRowTitles.map((rowTitle, index) => (
            <Line
              key={`line-${index}`}
              type="monotone"
              dataKey={rowTitle}
              stroke={data[0][rowTitle]?.color || "#206c7f"}
              strokeWidth={2}
              activeDot={{ r: 8 }}
              strokeOpacity={hoveredLine && hoveredLine !== rowTitle ? 0.1 : 1}
              filter={
                hoveredLine === rowTitle
                  ? `url(#glow-${uniqueRowTitles.indexOf(rowTitle)})`
                  : undefined
              }
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export default MatrixDevelopmentLineChart;

